/*
Hero carousel: native horizontal scroll with snap does the sliding (and
free swipe on touch); JS wires the arrows and dots.

The loop is endless in both directions. Cloned edge slides make the seam
invisible: a copy of the last slide sits before the first and a copy of the
first sits after the last, so 1→prev and 5→next both animate one ordinary
step into a clone — then, once the scroll settles, an instant (imperceptible)
teleport lands on the matching real slide. No fast rewind across the track.

Positions count slides including the clones: 0 = head clone, 1…n = real
slides, n+1 = tail clone (loopMath.js).
*/

import afterLoad from './afterLoad.js';
import addEdgeClones, { onScrollSettle } from './edgeClones.js';
import { itemAt, realTwin } from './loopMath.js';

const CLONES_PER_SIDE = 1;
// Auto-advance every 7 s, forever — the clones make 5→1 a forward step.
const AUTO_MS = 7000;

// Stops the previous carousel's timer and window/document listeners. PJAX swaps
// the page content but keeps the document, so they would otherwise pile up.
let teardown = null;

export default function heroCarousel() {
    const root = document.querySelector('[data-hero-carousel]');

    // bfcache brings the page back with its live carousel (timer included) — keep it.
    if (root?.dataset.heroReady) {
        return;
    }
    teardown?.();
    teardown = null;
    const parts = findParts(root);

    if (!parts) {
        return;
    }
    root.dataset.heroReady = 'true';
    const lifetime = new AbortController();

    startCarousel(parts, lifetime.signal);
    teardown = () => lifetime.abort();
}

// A carousel needs its track, at least two real slides and the dots holder.
function findParts(root) {
    const track = root?.querySelector('[data-hero-track]');
    const slides = track ? [...track.querySelectorAll('.th-hero:not([data-hero-clone])')] : [];
    const dotsWrap = root?.querySelector('[data-hero-dots]');

    if (!track || slides.length < 2 || !dotsWrap) {
        return null;
    }

    return { root, track, slides, dotsWrap };
}

function startCarousel({ root, track, slides, dotsWrap }, signal) {
    const count = slides.length;

    addEdgeClones(track, slides, { perSide: CLONES_PER_SIDE, attribute: 'data-hero-clone' });
    afterLoad(() => {
        if (!signal.aborted) {
            preloadSlideImages(track);
        }
    });
    const slider = createSlider(track, count);
    const autoAdvance = createAutoAdvance(() => slider.moveTo(slider.position() + 1), signal);
    // A move by the visitor restarts the auto-advance countdown.
    const moveByUser = (position) => {
        slider.moveTo(position);
        autoAdvance.restart();
    };

    // Fresh load starts at 0 — that's the head clone; land on slide 1.
    if (slider.isOnHeadClone()) {
        slider.jumpTo(CLONES_PER_SIDE);
    }
    const dots = buildDots(dotsWrap, count, (index) => moveByUser(index + CLONES_PER_SIDE));

    root.querySelector('[data-hero-prev]')
        ?.addEventListener('click', () => moveByUser(slider.position() - 1));
    root.querySelector('[data-hero-next]')
        ?.addEventListener('click', () => moveByUser(slider.position() + 1));
    const current = followScroll({ track, slider, dots }, signal);

    // A width change would strand the fractional scroll position.
    window.addEventListener('resize', () => slider.jumpTo(current() + CLONES_PER_SIDE), { signal });
    // Any manual act (arrows, dots, swipe, trackpad) restarts the countdown.
    track.addEventListener('pointerdown', autoAdvance.restart, { passive: true, signal });
    track.addEventListener('wheel', autoAdvance.restart, { passive: true, signal });
    autoAdvance.restart();
}

// Moves go through here. A smooth move is a real slide change (auto-advance,
// arrows, dots); an instant jump only lands on slide 1 or swaps a clone for its twin.
function createSlider(track, count) {
    const width = () => track.clientWidth;
    const scrollToPosition = (position, behavior) => {
        track.scrollTo({ left: position * width(), behavior });
    };
    const position = () => Math.round(track.scrollLeft / width());

    return {
        position,
        item: () => itemAt(position(), CLONES_PER_SIDE, count),
        twin: () => realTwin(position(), CLONES_PER_SIDE, count),
        isOnHeadClone: () => track.scrollLeft < width() / 2,
        moveTo: (target) => scrollToPosition(target, 'smooth'),
        jumpTo: (target) => scrollToPosition(target, 'auto'),
    };
}

/**
 * Keeps the dots on the shown slide while the track scrolls, and swaps a clone for
 * its twin once the scroll settles.
 * @returns {() => number} The slide (0-based) the dots last showed.
 */
function followScroll({ track, slider, dots }, signal) {
    let current = slider.item();
    // A frame requested just before the carousel is torn down still fires — skip it.
    const sync = () => {
        if (signal.aborted) {
            return;
        }
        current = slider.item();
        markCurrentDot(dots, current);
    };

    track.addEventListener('scroll', () => requestAnimationFrame(sync), { passive: true, signal });
    onScrollSettle(track, () => {
        const twin = slider.twin();

        if (twin !== null) {
            slider.jumpTo(twin);
        }
    }, signal);
    sync();

    return () => current;
}

function buildDots(dotsWrap, count, onPick) {
    return Array.from({ length: count }, (_, index) => {
        const dot = document.createElement('button');

        dot.type = 'button';
        dot.className = 'th-hero-dot-btn';
        dot.setAttribute('aria-label', `Story ${index + 1} of ${count}`);
        dot.addEventListener('click', () => onPick(index));
        dotsWrap.appendChild(dot);
        return dot;
    });
}

function markCurrentDot(dots, current) {
    dots.forEach((dot, index) => dot.setAttribute('aria-current', String(index === current)));
}

// Steps every AUTO_MS unless the visitor prefers reduced motion; a hidden tab skips
// the step, and coming back restarts the countdown. The signal stops it for good.
function createAutoAdvance(step, signal) {
    let timer = null;
    const restart = () => {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            return;
        }
        clearTimeout(timer);
        timer = setTimeout(() => {
            if (!document.hidden) {
                step();
            }
            restart();
        }, AUTO_MS);
    };

    document.addEventListener('visibilitychange', () => {
        if (!document.hidden) {
            restart();
        }
    }, { signal });
    signal.addEventListener('abort', () => clearTimeout(timer), { once: true });

    return { restart };
}

// Slides 2…n arrive with loading="lazy" (partials/hero-picture.hbs), so they
// never compete with slide 1. Once the page has loaded they are fetched anyway:
// lazy loading inside a horizontal scroller starts too late for auto-advance.
function preloadSlideImages(track) {
    track.querySelectorAll('img[loading="lazy"]').forEach((img) => {
        img.loading = 'eager';
    });
}
